/**
 * Global notification manager service
 * Provides centralized notification handling across the application
 */

import type { Notification } from "../types";

export class NotificationManager {
  private static instance: NotificationManager;
  private subscribers: Array<(notification: Notification) => void> = [];
  private soundEnabled = false;

  private constructor() {}

  static getInstance(): NotificationManager {
    if (!NotificationManager.instance) {
      NotificationManager.instance = new NotificationManager();
    }
    return NotificationManager.instance;
  }

  /**
   * Subscribe to notification events
   */
  subscribe(callback: (notification: Notification) => void): () => void {
    this.subscribers.push(callback);

    // Return unsubscribe function
    return () => {
      this.subscribers = this.subscribers.filter((sub) => sub !== callback);
    };
  }

  /**
   * Handle incoming notification
   */
  handleNotification(notification: Notification) {
    // Notify all subscribers
    this.subscribers.forEach((callback) => {
      try {
        callback(notification);
      } catch (error) {
        console.error("Error in notification subscriber:", error);
      }
    });

    // Show browser notification for high priority
    if (notification.priority === "URGENT" || notification.priority === "HIGH") {
      this.showBrowserNotification(notification);
    }

    // Play sound for urgent notifications
    if (notification.priority === "URGENT" && this.soundEnabled) {
      this.playNotificationSound();
    }
  }

  /**
   * Enable/disable notification sounds
   */
  setSoundEnabled(enabled: boolean) {
    this.soundEnabled = enabled;
  }

  /**
   * Request browser notification permission
   * @deprecated Use NotificationPermissionDialog component for better mobile UX
   */
  async requestNotificationPermission(): Promise<boolean> {
    console.warn(
      "NotificationManager.requestNotificationPermission is deprecated. " +
        "Use NotificationPermissionDialog component for better mobile UX."
    );

    if (!("Notification" in window)) {
      return false;
    }

    if (Notification.permission === "granted") {
      return true;
    }

    if (Notification.permission === "default") {
      const permission = await Notification.requestPermission();
      return permission === "granted";
    }

    return false;
  }

  /**
   * Show browser notification
   */
  private showBrowserNotification(notification: Notification) {
    if (!("Notification" in window) || Notification.permission !== "granted") {
      return;
    }

    const browserNotification = new Notification(notification.title, {
      body: notification.message,
      icon: "/favicon.ico",
      tag: notification.id,
      requireInteraction: notification.priority === "URGENT",
      data: {
        url: notification.actionUrl,
        notificationId: notification.id,
      },
    });

    // Handle click events
    browserNotification.onclick = () => {
      if (notification.actionUrl) {
        if (notification.actionUrl.startsWith("http")) {
          window.open(notification.actionUrl, "_blank", "noopener,noreferrer");
        } else {
          window.focus();
          window.location.href = notification.actionUrl;
        }
      } else {
        window.focus();
      }
      browserNotification.close();
    };

    // Auto-close after delay for non-urgent notifications
    if (notification.priority !== "URGENT") {
      setTimeout(() => {
        browserNotification.close();
      }, 5000);
    }
  }

  /**
   * Play notification sound
   */
  private playNotificationSound() {
    try {
      if ("AudioContext" in window) {
        const audioContext = new AudioContext();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        // Create a pleasant notification sound
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
        oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.2);

        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.3);
      }
    } catch (_error) {}
  }

  /**
   * Get current notification permission status
   */
  getNotificationPermission(): NotificationPermission | "unsupported" {
    if (!("Notification" in window)) {
      return "unsupported";
    }
    return Notification.permission;
  }

  /**
   * Check if browser notifications are supported and enabled
   */
  isBrowserNotificationEnabled(): boolean {
    return "Notification" in window && Notification.permission === "granted";
  }
}

// Export singleton instance
export const notificationManager = NotificationManager.getInstance();
